import { useState, useEffect, useCallback } from 'react';

const API_BASE = 'https://api.cloudnine.biz.id';

/**
 * Custom hook untuk mengambil dan mengelola storyboards (Note List)
 */
export const useStoryboards = (parentId = null) => {
    const [storyboards, setStoryboards] = useState([]);
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch storyboards
    const fetchStoryboards = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const url = `${API_BASE}/storyboards.php${parentId ? `?parent_id=${parentId}` : ''}`;
            const response = await fetch(url);
            const result = await response.json();
            if (result.success) {
                setStoryboards(result.data || []);
            } else {
                throw new Error(result.error || 'Failed to fetch storyboards');
            }
        } catch (err) {
            setError(err.message);
            console.error('Error fetching storyboards:', err);
        } finally {
            setLoading(false);
        }
    }, [parentId]);

    // Fetch all tags
    const fetchTags = useCallback(async () => {
        try {
            const response = await fetch(`${API_BASE}/tags.php`);
            const result = await response.json();
            if (result.success) {
                setTags(result.data || []);
            }
        } catch (err) {
            console.error('Error fetching tags:', err);
        }
    }, []);

    // Add storyboard
    const addStoryboard = async (data) => {
        try {
            const response = await fetch(`${API_BASE}/storyboards.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            const result = await response.json();
            if (result.success) {
                await fetchStoryboards();
                return result.data;
            } else {
                throw new Error(result.error || 'Failed to add storyboard');
            }
        } catch (err) {
            setError(err.message);
            console.error('Error adding storyboard:', err);
            return null;
        }
    };

    // Update storyboard
    const updateStoryboard = async (data) => {
        try {
            const response = await fetch(`${API_BASE}/storyboards.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            const result = await response.json();
            if (result.success) {
                await fetchStoryboards();
                return result.data;
            } else {
                throw new Error(result.error || 'Failed to update storyboard');
            }
        } catch (err) {
            setError(err.message);
            console.error('Error updating storyboard:', err);
            return null;
        }
    };

    // Delete storyboard
    const deleteStoryboard = async (id) => {
        try {
            const response = await fetch(`${API_BASE}/storyboards.php?id=${id}&action=delete`, {
                method: 'POST',
            });
            const result = await response.json();
            if (result.success) {
                await fetchStoryboards();
                return true;
            } else {
                throw new Error(result.error || 'Failed to delete storyboard');
            }
        } catch (err) {
            setError(err.message);
            console.error('Error deleting storyboard:', err);
            return false;
        }
    };

    // Toggle Pin Storyboard
    const togglePinStoryboard = async (id) => {
        try {
            const response = await fetch(`${API_BASE}/storyboards.php?id=${id}&action=toggle_pin`, {
                method: 'POST',
            });
            const result = await response.json();
            if (result.success) {
                await fetchStoryboards();
                return true;
            }
            return false;
        } catch (err) {
            console.error('Error toggling pin:', err);
            return false;
        }
    };

    // Add tag
    const addTag = async (data) => {
        try {
            const response = await fetch(`${API_BASE}/tags.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            const result = await response.json();
            if (result.success) {
                await fetchTags();
                return result.data;
            }
            return null;
        } catch (err) {
            console.error('Error adding tag:', err);
            return null;
        }
    };

    useEffect(() => {
        fetchStoryboards();
        fetchTags();
    }, [fetchStoryboards, fetchTags]);

    return {
        storyboards,
        tags,
        loading,
        error,
        refetch: fetchStoryboards,
        addStoryboard,
        updateStoryboard,
        deleteStoryboard,
        togglePinStoryboard,
        addTag,
        refetchTags: fetchTags,
    };
};
